import { useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export function useReCaptcha() {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const getReCaptchaToken = useCallback(async (action: string): Promise<string> => {
    // Skip reCAPTCHA in development
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('Skipping reCAPTCHA verification in development');
      return 'development-mode-bypass-token';
    }

    if (!executeRecaptcha) {
      console.error('reCAPTCHA context is not available');
      throw new Error('reCAPTCHA is not initialized');
    }

    try {
      console.log('Executing reCAPTCHA for action:', action);
      
      // Ensure action is URL-safe
      const safeAction = encodeURIComponent(action);
      console.log('URL-safe action:', safeAction);

      // Add delay to ensure reCAPTCHA is fully loaded
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Attempt to get token with retries
      let token: string | null = null;
      let retryCount = 0;
      const maxRetries = 3;
      let lastError: Error | null = null;

      while (!token && retryCount < maxRetries) {
        try {
          console.log(`Attempting to get reCAPTCHA token (attempt ${retryCount + 1}/${maxRetries})`);
          token = await executeRecaptcha(safeAction);
          console.log('reCAPTCHA token obtained:', token ? 'Yes' : 'No');
          
          if (!token) {
            throw new Error('Empty token received');
          }
          
          return token;
        } catch (error) {
          lastError = error instanceof Error ? error : new Error('Unknown error');
          console.error(`reCAPTCHA attempt ${retryCount + 1} failed:`, {
            name: lastError.name,
            message: lastError.message,
            stack: lastError.stack,
            action: safeAction
          });

          retryCount++;
          
          if (retryCount < maxRetries) {
            await new Promise(resolve => 
              setTimeout(resolve, Math.pow(2, retryCount) * 1000)
            );
          }
        }
      }

      throw new Error(
        `Failed to obtain reCAPTCHA token after ${maxRetries} attempts. ` +
        `Last error: ${lastError?.message}`
      );
    } catch (error) {
      console.error('reCAPTCHA error:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        action
      });

      throw error;
    }
  }, [executeRecaptcha]);

  return { getReCaptchaToken };
}

export default useReCaptcha;